import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReplaySubject, Subject, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs';

import { SocialFileService } from 'app/modules/social/services/social-file/social-file.service';
import { CustomValidators } from 'app/utils/custom.validators';
import { Loggable } from 'app/utils/logging/loggable';
import CTAItem from 'entity/CTAItem';
import SocialFile from 'entity/SocialFile';
import ButtonSize from 'enums/ButtonSize';

import { ModalColorPickerComponent, ModalDialogData } from '../modal-color-picker/modal-color-picker.component';

export interface UploadImage {
  selectedImage: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  imageBytes: any;
  imageHeight: number;
  imageWidth: number;
}

interface UploadFile {
  fileName: string;
  fileType: string;
  fileSize: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fileBytes: any;
}

export interface ModalCreateItemDialogData {
  scootaverseId: string;
  ctaItem?: CTAItem;
}

interface ContentInfo {
  urlLink?: string;
  uploadedFiles?: UploadFile[];
  selectedFiles?: SocialFile[];
}

interface ButtonInfo {
  buttonText?: string;
  buttonSize?: ButtonSize;
  buttonColor?: string;
  buttonTextColor?: string;
}

export interface ModalCreateItemDialogResponse {
  itemName?: string;
  itemImage?: UploadImage;
  hasContent?: boolean;
  contentInfo?: ContentInfo;
  hasButton?: boolean;
  buttonInfo?: ButtonInfo;
  itemIsTraversable?: boolean;
  isEdit?: boolean;
  CTAItemId?: number;
  filesArray?: SocialFile[];
}

@Component({
  selector: 'app-modal-create-item',
  templateUrl: './modal-create-item.component.html',
  styleUrls: ['./modal-create-item.component.scss'],
  standalone: false,
})
export class ModalCreateItemComponent extends Loggable implements OnDestroy, OnInit {
  public closed: Subject<boolean> = new Subject<boolean>();
  private destroyed$ = new ReplaySubject<boolean>(1);

  public itemName = new UntypedFormControl('');
  public addContent = new UntypedFormControl(false);
  public addButton = new UntypedFormControl({ value: false, disabled: true });
  public itemTraversable = new UntypedFormControl(false);
  public buttonText = new UntypedFormControl('');
  public sliderControl = new UntypedFormControl(0);
  public urlLink = new UntypedFormControl('', [CustomValidators.isValidUrl()]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('uploadedFilesTable') public uploadedFilesTable: any;
  @ViewChild('uploaderFile') public uploaderFile!: ElementRef;
  @ViewChild('uploaderImg') public uploaderImg!: ElementRef;

  protected MOOD_MAPPING: Record<number, string> = {
    0: 'Small',
    1: 'Medium',
    2: 'Large',
  };

  public state$ = this.sliderControl.valueChanges.pipe(
    startWith(this.sliderControl.value),
    map((value) => this.MOOD_MAPPING[value]),
  );

  public buttonColor = '#091d50';
  public textColor = '#ffffff';
  private _buttonPreviewValue?: string;

  public displayedColumns: string[] = ['name', 'type', 'action'];
  public displayedColumnsFiles: string[] = ['name', 'type', 'select', 'action'];
  protected fileUploaded = false;
  protected imageSelected = false;
  public imageLogoId?: string;
  public originalImageFile = new Image();
  protected imageData?: {
    name: string;
    width?: number;
    height?: number;
    extension: string;
  };
  protected fileData?: {
    name: string;
    extension: string;
  };
  public selectedImage?: string;

  private uploadedImage?: UploadImage;

  protected urlFieldHasValue = false;
  protected filesArray: SocialFile[] = [];
  protected uploadedFiles: UploadFile[] = [];

  protected selectedTab = 0;

  private modalResponse: ModalCreateItemDialogResponse = {};
  protected isEditItem = false;
  protected selectedFiles: SocialFile[] = [];
  private ctaItemId?: number;

  protected imageLabel =
    'Adding content (documents, images, links) will allow users to download or navigate to additional media by clicking on your room item.';

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalCreateItemDialogData,
    public dialogRef: MatDialogRef<ModalCreateItemDialogResponse>,
    private dialog: MatDialog,
    private socialFileService: SocialFileService,
  ) {
    super();
    this.setDisplayName('ModalCreateItemComponent');
  }

  public async ngOnInit(): Promise<void> {
    await this.loadFiles();

    this.buttonText.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe(() => {
      if (this.buttonText) {
        this.checkOverflow();
      }
    });

    if (this.data.ctaItem) {
      this.isEditItem = true;
      await this.handleEditItem(this.data.ctaItem);
    }
  }

  public shouldCreateAccount(value: boolean): void {
    this.closed.next(value);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public close(): void {
    this.dialogRef.close();
  }

  public confirm(): void {
    this.modalResponse.itemName = this.itemName.value;
    this.modalResponse.itemImage = this.uploadedImage;
    this.modalResponse.itemIsTraversable = this.itemTraversable.value;
    this.modalResponse.hasContent = this.addContent.value;
    this.modalResponse.hasButton = this.addButton.value;
    this.modalResponse.isEdit = this.isEditItem;

    if (this.isEditItem) {
      this.modalResponse.CTAItemId = this.ctaItemId;
      this.modalResponse.filesArray = this.filesArray;
    }

    if (this.modalResponse.hasContent) {
      this.modalResponse.contentInfo = {
        urlLink: this.urlLink.value,
        uploadedFiles: this.uploadedFiles,
        selectedFiles: this.selectedFiles,
      };
    }

    if (this.modalResponse.hasButton) {
      this.modalResponse.buttonInfo = {
        buttonText: this.buttonText.value,
        buttonColor: this.buttonColor,
        buttonTextColor: this.textColor,
        buttonSize: this.sliderControl.value,
      };
    }

    if (this.modalResponse) {
      this.dialogRef.close(this.modalResponse);
    }
  }

  public uploadFile(event?: Event): void {
    if (event) {
      const file = (event.target as HTMLInputElement)?.files?.[0];

      if (file) {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = () => {
          this.fileUploaded = true;
          this.uploadedFiles.push({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            fileBytes: reader.result,
          });
          if (this.uploadedFilesTable) {
            this.uploadedFilesTable.renderRows();
          }
          // Clear input so the user can upload the same file twice in a row
          this.uploaderFile.nativeElement.value = '';
        };
      }
    }
  }

  public uploadImage(event?: Event): void {
    if (event) {
      const file = (event.target as HTMLInputElement)?.files?.[0];
      this.handleImageReader(file);
    }
  }

  public removeImage(): void {
    this.imageData = {
      name: '',
      extension: '',
    };
    this.imageSelected = false;
    this.selectedImage = '';
    this.uploadedImage = {
      selectedImage: '',
      imageBytes: '',
      imageHeight: 0,
      imageWidth: 0,
    };
  }

  public openColorPickerDialog(colorForButton: boolean): void {
    const data: ModalDialogData = {
      color: colorForButton ? this.buttonColor : this.textColor,
    };

    this.dialog
      .open<ModalColorPickerComponent, ModalDialogData>(ModalColorPickerComponent, {
        data,
        width: '350px',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((color: string) => {
        if (color) {
          //If the user didn't select a color, the previous chose is keep
          if (colorForButton) {
            this.buttonColor = color;
          } else {
            this.textColor = color;
          }
        }
      });
  }

  public enableCreateItemBtn(): boolean {
    const addContent = this.addContent.value;
    const addButton = this.addButton.value;

    if (addContent && addButton) {
      return this.checkMandatoryContent && this.checkContentSection && this.checkButtonSection;
    } else if (addContent) {
      return this.checkMandatoryContent && this.checkContentSection;
    } else if (addButton) {
      return this.checkMandatoryContent && this.checkButtonSection;
    } else {
      return this.checkMandatoryContent;
    }
  }

  private get checkContentSection(): boolean {
    return this.urlLink.value || this.filesSelectedLength > 0 || this.uploadedFilesLength > 0;
  }

  private get checkButtonSection(): boolean {
    return this.buttonText.value.length > 0;
  }

  private get checkMandatoryContent(): boolean {
    return this.itemName.value && this.imageSelected;
  }

  private async loadFiles(): Promise<void> {
    if (this.data.scootaverseId) {
      this.filesArray = await this.socialFileService.getFiles(this.data.scootaverseId);
    }
  }

  public enableButtonSection(): void {
    if (this.addContent.value) {
      this.addButton.enable();
    } else {
      this.addButton.disable();
      this.addButton.setValue(false);
      this.buttonText.setValue('');
    }
  }

  public resetContent(): void {
    this.urlLink.setValue('');
    this.uploadedFiles = [];
    this.selectedFiles = [];
  }

  public removeUploadedFile(index: number): void {
    this.uploadedFiles.splice(index, 1);

    if (this.uploadedFilesTable) {
      this.uploadedFilesTable.renderRows();
    }
  }

  public formatFileType(filename: string): string {
    return filename.substring(filename.lastIndexOf('.') + 1, filename.length) || filename;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  public isAllSelected() {
    const numSelected = this.filesSelectedLength;
    const numRows = this.filesArray.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  public toggleAllRows() {
    if (this.selectedFiles.length === this.filesArray.length) {
      this.selectedFiles = [];
      return;
    }

    this.filesArray.forEach((file) => {
      const itemIndex = this.selectedFiles.findIndex((i) => file.socialFileId === i.socialFileId);
      if (itemIndex < 0) {
        this.selectedFiles.push(file);
      }
    });
  }

  public get filesSelectedLength(): number {
    return this.selectedFiles.length;
  }

  public get uploadedFilesLength(): number {
    return this.uploadedFiles.length;
  }

  public getButtonClass(option: number): string {
    switch (option) {
      case 0:
        return 'small-btn';
      case 1:
        return 'medium-btn';
      case 2:
        return 'large-btn';
      default:
        return '';
    }
  }

  private async handleEditItem(item: CTAItem): Promise<void> {
    this.itemName.setValue(item.name);

    this.itemTraversable.setValue(item.traversable);
    this.ctaItemId = item.CTAItemId;

    const file = await this.getImageFromURI(item.imageURI);

    if (file) {
      this.handleImageReader(file);
    }
    await this.getFilesFromItem(item.CTAItemId);

    if (item.buttonVisible) {
      this.textColor = item.buttonTextColor ?? '';
      this.buttonColor = item.buttonColor ?? '';
      this.buttonText.setValue(item.text, { emitEvent: true });

      this.sliderControl.setValue(item.buttonSize);

      this.addContent.setValue(true);

      if (item.url) {
        this.urlLink.setValue(item.url);
      }
      this.addButton.enable();
      this.addButton.setValue(true);
    }
  }

  private async getFilesFromItem(itemId?: number): Promise<void> {
    if (itemId) {
      const itemFiles = await this.socialFileService.getCTAItemFiles(itemId);
      if (itemFiles.length > 0) {
        this.addContent.setValue(true);
        this.selectedFiles.push(...itemFiles);
        this.selectedTab = 1;
        this.addButton.enable();
      } else {
        this.selectedTab = 0;
        this.addButton.disable();
        this.addButton.setValue(false);
      }
    }
  }

  public checkFileSelected(file: SocialFile): boolean {
    const isSelected = this.selectedFiles.find((i) => i.socialFileId === file.socialFileId);
    return isSelected ? true : false;
  }

  // Function needed to handle open edit CTA, since the image is returned as URL
  private async getImageFromURI(uri?: string) {
    if (uri) {
      try {
        const fileName = uri.split('/').at(-1);
        const fileType = uri.split('.').at(-1);
        const response = await fetch(uri);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        const blob = await response.blob();
        const file = new File([blob], fileName ?? '', { type: `application/${fileType}` });
        return file;
      } catch (error) {
        this.error('Error converting URL to file: ', error);
      }
    }
  }

  private handleImageReader(file?: File): void {
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        this.selectedImage = reader.result as string;
        const block = this.selectedImage.split(';');
        const base64 = block[1].split(',')[1];

        this.uploadedImage = {
          selectedImage: reader.result as string,
          imageBytes: base64,
          imageHeight: 220,
          imageWidth: 220,
        };

        this.imageData = {
          name: file.name,
          extension: file.type,
        };
        this.imageSelected = true;

        // Clear input so the user can upload the same file twice in a row
        this.uploaderImg.nativeElement.value = '';
      };
    }
  }

  public selectFile(row: SocialFile) {
    const itemIndex = this.selectedFiles.findIndex((file) => file.socialFileId === row.socialFileId);

    if (itemIndex > -1) {
      this.selectedFiles.splice(itemIndex, 1);
    } else {
      this.selectedFiles.push(row);
    }
  }

  private checkOverflow(): void {
    const maxLength = 14;
    const text = this.buttonText.value;

    this._buttonPreviewValue = text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  }

  public get buttonPreviewValue(): string {
    return this._buttonPreviewValue ?? '';
  }
}
